import { useCallback } from 'react';

import { useApolloClient, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import to from 'await-to-js';

import { notify } from '@/components/toast';
import {
  BATCH_JOB_QUERY,
  RETRY_BATCH_ITEM_MUTATION
} from '@/gql/queries/batch-action.gql';

type UseRetryBatchItemOptions = {
  // Called after a successful (settled) retry so the caller can drop the row's
  // pending state. Receives the item's new status ('DONE' | 'FAILED').
  onSettled?: (traceId: string, status: string) => void;
};

/**
 * Synchronous single-item retry (STMN-3361). Re-runs ONE failed trace of an
 * existing batch job in place — no new job, no new drawer — via the thin
 * `retryBatchItem` mutation (server-side signing, same as the batch engine).
 * On success it refetches the job so the drawer's counters + the failed row
 * update. Returns the settled status, or null on transport error.
 */
export function useRetryBatchItem(options?: UseRetryBatchItemOptions) {
  const [retryBatchItemMutation] = useMutation(RETRY_BATCH_ITEM_MUTATION);
  const apollo = useApolloClient();

  const retryBatchItem = useCallback(
    async (jobId: string, traceId: string): Promise<string | null> => {
      const promise = retryBatchItemMutation({
        variables: { input: { jobId, traceId } }
      });

      const [err, result] = await to(promise);
      if (err) {
        Sentry.captureException(err, {
          tags: { component: 'useRetryBatchItem' },
          extra: { jobId, traceId }
        });
        notify.error('Something went wrong while retrying this item...');
        options?.onSettled?.(traceId, 'FAILED');
        return null;
      }

      const status = result?.data?.retryBatchItem?.status ?? null;
      // Refresh THIS job so the drawer counters (processed/succeeded/failed)
      // update. useBatchJob stops polling once terminal (which it is when the
      // retry button shows), so without this the drawer would not reflect the
      // flip. Targeted by variables (not `include`) so it never warns about an
      // inactive query and always hits the right job.
      await apollo.query({
        query: BATCH_JOB_QUERY,
        variables: { id: jobId },
        fetchPolicy: 'network-only'
      });
      if (status) {
        options?.onSettled?.(traceId, status);
      }
      return status;
    },
    [retryBatchItemMutation, apollo, options?.onSettled]
  );

  return retryBatchItem;
}
